import { TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import SmartIcon from "../../../../components/generic/genericIcon/SmartIcon";
import PrimaryButton from "../../../../components/generic/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/generic/buttons/SecondaryButton";

const EditableGroupDetails = ({ title, topic, isAdmin = false, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [titleDraft, setTitleDraft] = useState(title ?? "");
  const [topicDraft, setTopicDraft] = useState(topic ?? "");
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setTitleDraft(title ?? "");
      setTopicDraft(topic ?? "");
    }
  }, [isEditing, title, topic]);

  const displayTitle = title?.trim();
  const displayTopic = topic?.trim();
  const isEmpty = !displayTitle && !displayTopic;

  const startEditing = () => {
    if (!isAdmin || isEditing) {
      return;
    }

    setTitleDraft(title ?? "");
    setTopicDraft(topic ?? "");
    setError("");
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setTitleDraft(title ?? "");
    setTopicDraft(topic ?? "");
    setError("");
    setIsEditing(false);
  };

  const saveEditing = async () => {
    const nextTitle = titleDraft.trim();
    const nextTopic = topicDraft.trim() || null;

    if (!nextTitle) {
      setError("Title is required");
      return;
    }

    const currentTitle = title?.trim() || null;
    const currentTopic = topic?.trim() || null;

    if (currentTitle === nextTitle && currentTopic === nextTopic) {
      cancelEditing();
      return;
    }

    setIsSaving(true);
    try {
      const success = await onSave({ title: nextTitle, topic: nextTopic });
      if (success !== false) {
        setError("");
        setIsEditing(false);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldChange = (setter) => (event) => {
    setter(event.target.value);
    if (error) {
      setError("");
    }
  };

  return (
    <div
      className={`group-chat-editable-field group-chat-group-details ${isEditing ? "is-editing" : ""} ${
        isAdmin ? "is-editable" : ""
      }`}
    >
      <div className="group-chat-editable-field-label">Group details</div>

      {isEditing ? (
        <div className="group-chat-editable-field-editor group-chat-group-details-editor">
          <TextField
            required
            fullWidth
            value={titleDraft}
            name="title"
            label="Title"
            variant="outlined"
            disabled={isSaving}
            error={Boolean(error)}
            helperText={error}
            onChange={handleFieldChange(setTitleDraft)}
            sx={{ maxWidth: "100%" }}
          />
          <TextField
            fullWidth
            value={topicDraft}
            name="topic"
            label="Topic"
            variant="outlined"
            disabled={isSaving}
            multiline
            rows={2}
            onChange={handleFieldChange(setTopicDraft)}
            sx={{
              maxWidth: "100%",
              height: "auto",
              "& .MuiInputBase-root": { height: "auto", maxWidth: "100%" },
            }}
          />

          <div
            className="group-chat-editable-field-actions"
          >
            <SecondaryButton
              buttonText="Cancel"
              handleOnClick={cancelEditing}
              buttonDisable={isSaving}
            />
            <PrimaryButton
              buttonText="Save"
              handleOnClick={saveEditing}
              loading={isSaving}
            />
          </div>
        </div>
      ) : isAdmin ? (
        <button
          type="button"
          className={`group-chat-editable-field-display group-chat-group-details-display ${
            isEmpty ? "is-empty" : ""
          }`}
          onClick={startEditing}
          aria-label="Edit group title and topic"
        >
          <span className="group-chat-group-details-display-content">
            <span className="group-chat-group-details-title">
              {displayTitle || "Add a group title"}
            </span>
            <span className="group-chat-group-details-topic">
              {displayTopic || "Add a topic (optional)"}
            </span>
          </span>
          <span className="group-chat-editable-field-edit-hint" aria-hidden>
            <SmartIcon
              iconKey="edit"
              width={15}
              height={15}
              color="var(--chat-accent-secondary)"
            />
          </span>
        </button>
      ) : (
        <div
          className={`group-chat-editable-field-display group-chat-group-details-display ${
            isEmpty ? "is-empty" : ""
          }`}
        >
          <span className="group-chat-group-details-display-content">
            <span className="group-chat-group-details-title">{displayTitle || "—"}</span>
            {displayTopic ? (
              <span className="group-chat-group-details-topic">{displayTopic}</span>
            ) : null}
          </span>
        </div>
      )}
    </div>
  );
};

export default EditableGroupDetails;
